import { useState } from 'react';
import { Flex, Form } from 'antd';
import TextField from '../form/text-field/text-field';
import styles from './security.module.scss';
import { Button } from '../button/button';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { passwordRules } from './security.utils';
import clsx from 'clsx';
import { InfoComponent } from '../info-component/info-component';

export type SecurityFieldType = {
  currentPassword: string;
  newPassword: string;
};

interface SecurityProps {
  onSubmit: (values: SecurityFieldType) => void;
  error?: string;
  isLoading?: boolean;
}

export function Security({ onSubmit, isLoading, error }: SecurityProps) {
  const [form] = Form.useForm();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [validations, setValidations] = useState(
    passwordRules.map((rule) => ({ ...rule, valid: false }))
  );
  const [formIsVisible, setIsFormVisible] = useState(false);

  const handleNewPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPasswordValue = e.target.value;
    setNewPassword(newPasswordValue);
    setValidations(
      passwordRules.map((rule) => ({ ...rule, valid: rule.regex.test(newPasswordValue) }))
    );
  };

  const isSaveDisabled =
    !currentPassword || !newPassword || validations.some((validation) => !validation.valid);

  return (
    <>
      {!formIsVisible ? (
        <Flex vertical className={styles.securityContainer}>
          <Flex className={styles.securityContainerButton}>
            <p className={styles.passwordTitle}>Password</p>
            <Button buttonVariant="secondary" onClick={() => setIsFormVisible(true)}>
              Change password
            </Button>
          </Flex>

          <InfoComponent text="We will log you out if you decide to change your password." />
        </Flex>
      ) : (
        <Form
          form={form}
          layout="vertical"
          className={styles.formContainer}
          onFinish={(values) => {
            onSubmit(values);
          }}
        >
          <Form.Item<SecurityFieldType>
            label="Current Password"
            name="currentPassword"
            rules={[{ required: true, message: 'Please input your current password!' }]}
          >
            <TextField
              data-testid="currentPassword"
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </Form.Item>

          <Form.Item<SecurityFieldType>
            className={styles.newPasswordContainer}
            label="New Password"
            name="newPassword"
            rules={[{ required: true, message: 'Please input your new password!' }]}
          >
            <TextField
              data-testid="newPassword"
              type="password"
              value={newPassword}
              onChange={handleNewPasswordChange}
            />
          </Form.Item>

          <div className={styles.passwordValidationContainer}>
            {validations.map(({ message, valid }) => (
              <div
                key={message}
                className={clsx(valid ? styles.validationItemValid : styles.validationItemInvalid)}
              >
                {valid ? (
                  <CheckCircleOutlined className={styles.validValidationIcon} />
                ) : (
                  <CloseCircleOutlined className={styles.invalidValidationIcon} />
                )}
                {message}
              </div>
            ))}
          </div>

          <Form.Item>
            <Flex gap={10} className={styles.formButtons}>
              <Button
                className={styles.saveButton}
                type="primary"
                htmlType="submit"
                loading={isLoading}
                disabled={isSaveDisabled}
              >
                Save
              </Button>
              <Button
                onClick={() => {
                  form.resetFields();
                  setValidations(passwordRules.map((rule) => ({ ...rule, valid: false })));
                  setIsFormVisible(false);
                }}
                buttonVariant="secondary"
                className={styles.cancelButton}
              >
                Cancel
              </Button>
            </Flex>

            {error && <p className={styles.error}>{error}</p>}
          </Form.Item>
        </Form>
      )}
    </>
  );
}
